"use client";
import { useEffect, useState } from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  Sun,
  Moon,
  LogOut,
  UserPlus,
  User,
  Loader,
  LayoutDashboard,
} from "lucide-react";
import { createSupabaseBrowserClient } from "@/lib/auth/supabase-browser";
import { useTheme } from "next-themes";
import Link from "next/link";
import { GenericAvatar } from "./GenericAvatar";

interface ProfileData {
  id: string;
  email: string;
  full_name: string | null;
  username: string | null;
  avatar_url: string | null;
  role: string;
}

interface MobileProfileBlockProps {
  onNavigate?: () => void;
  scheme?: "light" | "dark";
}

export function MobileProfileBlock({ onNavigate }: MobileProfileBlockProps) {
  const [profile, setProfile] = useState<ProfileData | null>(null);
  const [loading, setLoading] = useState(true);
  const supabase = createSupabaseBrowserClient();
  const { setTheme } = useTheme();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/api/auth/profile", { cache: "no-store" });
        if (res.ok) {
          const json = await res.json();
          if (!cancelled) setProfile(json.profile);
        } else if (!cancelled) setProfile(null);
      } catch {
        if (!cancelled) setProfile(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const isAuthed = !!profile;

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-3">
        <Avatar className="h-12 w-12 border bg-background/40">
          {loading ? (
            <AvatarFallback className="text-xs">
              <Loader className="animate-spin" />
            </AvatarFallback>
          ) : isAuthed ? (
            <>
              <AvatarImage src={profile?.avatar_url || undefined} />
              <AvatarFallback className="text-sm">
                <GenericAvatar
                  name={
                    profile?.full_name ||
                    profile?.username ||
                    profile?.email ||
                    "User"
                  }
                  email={profile?.email || "Anonymous"}
                  src={profile?.avatar_url || ""}
                  className="h-8 w-8"
                />
              </AvatarFallback>
            </>
          ) : (
            <AvatarFallback className="text-sm">?</AvatarFallback>
          )}
        </Avatar>
        <div className="flex flex-col min-w-0">
          <span className="text-sm font-medium truncate">
            {loading
              ? "Loading..."
              : isAuthed
              ? profile?.full_name || profile?.username || profile?.email
              : "Guest"}
          </span>
          <span className="text-xs text-muted-foreground truncate">
            {isAuthed ? profile?.email : "You are browsing anonymously"}
          </span>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2">
        <Button
          variant="outline"
          size="sm"
          className="flex flex-col items-center gap-1 h-16"
          onClick={() => setTheme("light")}
        >
          <Sun className="h-4 w-4" />
          <span className="text-xs">Light</span>
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="flex flex-col items-center gap-1 h-16"
          onClick={() => setTheme("dark")}
        >
          <Moon className="h-4 w-4" />
          <span className="text-xs">Dark</span>
        </Button>
        <Button
          variant="outline"
          size="sm"
          className="flex flex-col items-center gap-1 h-16"
          onClick={() => setTheme("system")}
        >
          <Moon className="h-4 w-4" />
          <span className="text-xs">System</span>
        </Button>
      </div>
      {isAuthed ? (
        <div className="flex flex-col gap-2">
          <Button
            variant="ghost"
            size="sm"
            className="justify-start"
            onClick={() => {
              onNavigate?.();
              // navigate to profile/settings
            }}
            asChild
          >
            <Link href="/dashboard">
              <LayoutDashboard className="mr-2 h-4 w-4" /> Dashboard
            </Link>
          </Button>
          <Button
            variant="destructive"
            size="sm"
            className="justify-start"
            onClick={async () => {
              await supabase.auth.signOut();
              onNavigate?.();
              window.location.href = "/signin";
            }}
          >
            <LogOut className="h-4 w-4 mr-2" /> Logout
          </Button>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          <Button
            variant="default"
            size="sm"
            className="justify-start"
            onClick={() => {
              onNavigate?.();
              window.location.href = "/signin";
            }}
          >
            <UserPlus className="h-4 w-4 mr-2" /> Sign In
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="justify-start"
            onClick={() => {
              onNavigate?.();
              window.location.href = "/signup";
            }}
          >
            <User className="h-4 w-4 mr-2" /> Create Account
          </Button>
        </div>
      )}
    </div>
  );
}
